// The three success targets named in section 1.4 of the client scope.
//
//   Average time to first response   < 30 seconds
//   User satisfaction score          > 4.2 out of 5
//   Scam detection accuracy          > 90%
//
// Two of the three are NOT measured today, and this panel says so in words
// rather than drawing a zero or a full meter. What is on the screen has to be
// trustworthy before it is complete, so each row states what is actually being
// counted, not what the scope asked for.
//
//   Time to first response. Nothing times the bot's first reply. The only
//   first-response column in the product is agent_transfers.first_response_at,
//   which is a HUMAN agent picking up a transfer, is reported in MINUTES, and
//   is hardcoded to 0.0 in every aggregate the API returns. Reporting it as
//   "0 minutes, target met" would be the worst number on the console. The
//   contract for the endpoint that would fill this in is in the handover note.
//
//   User satisfaction. The survey asks a 0 to 10 Net Promoter question and the
//   stored score is a bucket (0-5 / 6-8 / 9-10) rolled up to an NPS between
//   -100 and +100. The scope asks for a mean out of 5 against a 4.2 target.
//   There is no arithmetic that turns one into the other: NPS discards the
//   within-bucket value and is a difference of proportions, not a mean. So the
//   NPS is shown, labelled as NPS, and the 4.2/5 target is marked unmeasured.
//
//   Scam detection accuracy. This one IS measured, on the held-out evaluation
//   benchmark rather than on live traffic, and the row says which.

import { useMemo } from "react";

import { http, qs } from "../api";
import { Badge, Banner, SkeletonRows, useAsync } from "../ui";
import { StatTile } from "../charts";
import { NotMeasuredTile, nf } from "./shared";

/** The NPS block of the chatbot analytics payload. */
export interface NpsSummaryLike {
  total_responses: number;
  detractors: number;
  passives: number;
  promoters: number;
  nps_score: number;
}

interface TrendPoint {
  run_id: string;
  ts?: string;
  suite?: string;
  n?: number;
  fraud_n?: number;
  fraud_tp?: number;
  fraud_fp?: number;
  fraud_fn?: number;
  fraud_recall?: number | null;
}

/**
 * Accuracy over the fraud-scoped cases of one run.
 *
 * The trend endpoint returns the confusion counts but not accuracy, so it is
 * derived here: every fraud-scoped row is one of TP, TN, FP or FN, therefore
 * TN = n - TP - FP - FN and accuracy = (n - FP - FN) / n. Null when the run
 * graded no fraud-scoped case, because a rate with no denominator is not zero.
 */
function runAccuracy(point: TrendPoint | null): number | null {
  if (!point) return null;
  const total = Number(point.fraud_n) || 0;
  if (total <= 0) return null;
  const wrong = (Number(point.fraud_fp) || 0) + (Number(point.fraud_fn) || 0);
  return (total - wrong) / total;
}

const ACCURACY_TARGET = 0.9;

export function ScopeTargetsPanel({
  nps,
  loading,
}: {
  nps: NpsSummaryLike | null;
  loading: boolean;
}) {
  // Detection runs only. A conversation run reports fraud_n 0 and every
  // detection rate null, so including it would put a hole in the headline.
  const trendQuery = useAsync(
    () => http.get<{ trend: TrendPoint[] }>(`/chatbot/eval-ops/trend${qs({ limit: 50 })}`),
    []
  );

  const latest = useMemo(() => {
    const points = (trendQuery.data?.trend ?? []).filter(
      (p) => p && (Number(p.fraud_n) || 0) > 0 && p.fraud_recall != null
    );
    return points.length ? points[points.length - 1] : null;
  }, [trendQuery.data]);

  const accuracy = runAccuracy(latest);
  const npsMeasured = Boolean(nps && nps.total_responses > 0);

  return (
    <div className="card" style={{ marginBottom: "var(--sp-4)" }}>
      <div className="row">
        <div>
          <h3>Scope success targets</h3>
          <div className="muted">
            Section 1.4 of the Cyber Concierge scope. Each row says what is actually being counted.
          </div>
        </div>
      </div>

      {trendQuery.error && (
        <Banner tone="warning" traceId={trendQuery.error.traceId}>
          The evaluation trend did not load, so detection accuracy is unavailable rather than zero:{" "}
          {trendQuery.error.message}
        </Banner>
      )}

      <div className="grid c3" style={{ marginTop: "var(--sp-3)" }}>
        {/* ---- target 1: time to first response under 30 seconds ---- */}
        <NotMeasuredTile
          label="Time to first response"
          why="target < 30s · nothing times the bot's first reply"
        />

        {/* ---- target 2: user satisfaction above 4.2 out of 5 ---- */}
        <NotMeasuredTile
          label="Satisfaction score out of 5"
          why="target > 4.2 · the survey asks a 0 to 10 NPS question"
        />

        {/* ---- target 3: scam detection accuracy above 90% ---- */}
        {trendQuery.loading ? (
          <div className="stat">
            <SkeletonRows n={2} cols={1} />
          </div>
        ) : accuracy === null ? (
          <NotMeasuredTile
            label="Scam detection accuracy"
            why="target > 90% · no graded detection run recorded"
          />
        ) : (
          <StatTile
            label="Scam detection accuracy"
            value={`${(accuracy * 100).toFixed(1)}%`}
            deltaLabel={`target > 90% · held-out benchmark, ${nf.format(
              Number(latest?.fraud_n) || 0
            )} cases`}
          />
        )}
      </div>

      <div className="col" style={{ marginTop: "var(--sp-4)" }}>
        <div className="row">
          <span className="strong">Time to first response</span>
          <span className="spacer" />
          <Badge tone="warning">not measured</Badge>
        </div>
        <div className="muted">
          The scope asks for the concierge's first reply in seconds. The only first-response field in
          the product records a human agent picking up a transfer, in minutes, and the API returns it
          as a constant 0.0. It is not shown here because showing it would report a target met that
          nobody has measured.
        </div>

        <div className="row" style={{ marginTop: "var(--sp-3)" }}>
          <span className="strong">User satisfaction</span>
          <span className="spacer" />
          {loading ? null : npsMeasured ? (
            <Badge tone="info">NPS {nps?.nps_score}</Badge>
          ) : (
            <Badge tone="warning">no responses</Badge>
          )}
        </div>
        <div className="muted">
          {npsMeasured
            ? `Measured as Net Promoter Score from ${nf.format(
                nps?.total_responses ?? 0
              )} responses on a 0 to 10 question, reported between -100 and +100. The scope asks for a mean out of 5 against a 4.2 target. NPS is a difference of proportions, not a mean, so the two are not convertible and the 4.2 target stays unmeasured until the survey collects a 1 to 5 rating.`
            : "No survey responses in this period, so there is no satisfaction figure of any kind. The scope's 4.2 out of 5 target also needs a 1 to 5 rating the survey does not yet ask for."}
        </div>

        <div className="row" style={{ marginTop: "var(--sp-3)" }}>
          <span className="strong">Scam detection accuracy</span>
          <span className="spacer" />
          {trendQuery.loading ? null : accuracy === null ? (
            <Badge tone="warning">not measured</Badge>
          ) : (
            <Badge tone={accuracy >= ACCURACY_TARGET ? "good" : "critical"}>
              {accuracy >= ACCURACY_TARGET ? "meets target" : "below target"}
            </Badge>
          )}
        </div>
        <div className="muted">
          {accuracy === null
            ? "No graded detection run is recorded, so there is no accuracy figure. Record one and this row fills in."
            : `Run ${latest?.run_id ?? ""} on the held-out fraud benchmark, promo-spam sources excluded. This is benchmark accuracy, not accuracy on live conversations: nothing labels live traffic, so production accuracy is not measured.`}
        </div>
      </div>
    </div>
  );
}
